// Server actions for screens (docs/backend/ENDPOINTS.md). In server mode `useAction` calls the endpoint and refreshes
// the collections it changes; in sample mode there is no server, so it resolves `{ mode: "sample" }` and the screen
// keeps its sample behaviour (usually the existing toast). Failures show the server's own message in a toast.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@offsilo/ui/toast";
import { m } from "@/paraglide/messages.js";
import type { CollectionName } from "./client";
import { ApiError, call } from "./server-client";

export const serverMode = import.meta.env.VITE_DATA === "server";

export type ActionResult<T> = { mode: "server"; data: T } | { mode: "sample" };
type Method = "POST" | "PUT" | "PATCH" | "DELETE" | "GET";

/** One action call. `path` is under /api/v1, e.g. `/approvals/ap-1/decide`. */
export async function act<T = unknown>(method: Method, path: string, body?: unknown): Promise<ActionResult<T>> {
  if (!serverMode) return { mode: "sample" };
  const data = await call<T>(path, {
    method,
    ...(body !== undefined && method !== "GET" ? { body: JSON.stringify(body) } : {}),
    headers: method === "GET" ? {} : { "idempotency-key": crypto.randomUUID() },
  });
  return { mode: "server", data };
}

/** The message to show for a failed action: the server's detail when it gave one. */
export function actionError(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { detail?: string; title?: string; fields?: { message: string }[] };
    return body.detail || body.fields?.[0]?.message || body.title || e.message || m.data_x13_action_failed();
  }
  // a rejection with no message still tells the person something went wrong
  return String((e as Error)?.message || e || "") || m.data_x13_action_failed();
}

type Vars<B> = { path: string; body?: B };

/**
 * A mutation for one action. `invalidate` lists the collections to refetch after it succeeds (realtime also
 * refreshes them, this makes the acting screen update at once). Errors toast and rethrow.
 *
 *   const decide = useAction("POST", { invalidate: ["approvals"] });
 *   const r = await decide.mutateAsync({ path: `/approvals/${id}/decide`, body: { decision: "approve" } });
 *   if (r.mode === "sample") toast(...existing sample toast...);
 */
/** `quiet`: the page shows the failure itself (a refusal banner), so the generic toast is skipped and only one message appears. */
export function useAction<T = unknown, B = unknown>(method: Method, opts: { invalidate?: CollectionName[]; quiet?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: Vars<B>) => act<T>(method, v.path, v.body),
    onSuccess: (r) => {
      if (r.mode !== "server") return;
      for (const c of opts.invalidate ?? []) {
        qc.invalidateQueries({ queryKey: ["list", c] });
        qc.invalidateQueries({ queryKey: ["chrome", c] });
        qc.invalidateQueries({ queryKey: ["row", c] });
      }
    },
    onError: (e) => {
      if (!opts.quiet) toast({ title: m.toast_save_failed(), description: actionError(e), tone: "danger" });
    },
  });
}
